<template>
  <section
    class="rounded-lg border border-gray-200 bg-white p-4"
    aria-labelledby="setup-call-banner-title"
    data-testid="setup-call-banner"
  >
    <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex min-w-0 items-center gap-3">
        <img
          :src="AR0231_SPECIALIST_AVATAR"
          alt=""
          class="size-10 shrink-0 rounded-full object-cover"
        />
        <div class="min-w-0">
          <p class="text-sm text-gray-600">
            {{ t('specialist') }}
          </p>
          <h2
            id="setup-call-banner-title"
            class="text-base font-semibold text-gray-900"
          >
            {{ t('title') }}
          </h2>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-3">
        <ZoneButton
          variant="outline"
          color="gray_alt"
          size-class="h-9 px-4 text-sm font-medium!"
          class="hover:bg-gray-100!"
          data-testid="setup-call-remind-later"
          @click="remindLater"
        >
          {{ t('remind_later') }}
        </ZoneButton>
        <ZoneButton
          color="primary_alt"
          size-class="h-9 px-4 text-sm font-medium!"
          :href="setupCallUrl"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="setup-call-schedule"
          @click="track('Scheduled')"
        >
          <template #leading-icon>
            <ZoneIcon
              icon="event_note"
              class="text-base!"
            />
          </template>
          {{ t('schedule_call') }}
        </ZoneButton>
      </div>
    </div>
    <div class="mt-4 grid grid-cols-1 items-stretch gap-3 md:grid-cols-3">
      <div
        v-for="topic in TOPICS"
        :key="topic.key"
        class="flex items-start gap-2 rounded-xl bg-gray-50 p-3"
        :data-testid="`setup-call-topic-${topic.key}`"
      >
        <ZoneIcon
          :icon="topic.icon"
          size="xxl"
          class="shrink-0 text-primary-700"
        />
        <div class="min-w-0 flex-1">
          <p class="text-base font-semibold text-gray-800">
            {{ t(`topics.${topic.key}.title`) }}
          </p>
          <p class="mt-1 text-sm text-gray-600">
            {{ t(`topics.${topic.key}.description`) }}
          </p>
          <div
            v-if="topic.key === 'integrations'"
            class="mt-2 flex items-center gap-3"
          >
            <img
              v-for="logo in LOGOS"
              :key="logo.name"
              :src="logo.src"
              :alt="logo.name"
              class="size-6 shrink-0 object-contain"
            />
            <span class="text-xs text-gray-600">{{ t('more_integrations') }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AnalyticsResource } from '~/resources';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import {
  AR0231_LOGO_CLICKUP,
  AR0231_LOGO_PAYPAL,
  AR0231_LOGO_SLACK,
  AR0231_LOGO_WISE,
  AR0231_SPECIALIST_AVATAR,
} from '~/rails_assets';

const { setupCallUrl, organizationId } = defineProps({
  setupCallUrl: { type: String, required: true },
  organizationId: { type: Number, required: true },
});

const emit = defineEmits(['remindLater']);

defineOptions({ name: 'ChecklistSetupCallBanner' });

const { t } = useI18n();

const track = (action) => AnalyticsResource.create(null, {
  event_name: `Getting Started Checklist 0231 Gap Team Setup Call ${action}`,
  event_properties: { experiment: true, source: 'getting started setup call', user_role: 'owner', organization_id: organizationId },
});

onMounted(() => track('Viewed'));

const remindLater = () => {
  track('Postponed');
  emit('remindLater');
};

const TOPICS = [
  { key: 'monitoring', icon: 'insert_chart' },
  { key: 'integrations', icon: 'check_circle' },
  { key: 'other', icon: 'lightbulb' },
];

const LOGOS = [
  { name: 'Wise', src: AR0231_LOGO_WISE },
  { name: 'Slack', src: AR0231_LOGO_SLACK },
  { name: 'ClickUp', src: AR0231_LOGO_CLICKUP },
  { name: 'PayPal', src: AR0231_LOGO_PAYPAL },
];
</script>

<i18n lang="json">
{
  "en": {
    "specialist": "Rebeca Isfer",
    "title": "Start your Hubstaff setup in 20 minutes",
    "remind_later": "Remind me later",
    "schedule_call": "Schedule a call",
    "more_integrations": "+20 others",
    "topics": {
      "monitoring": {
        "title": "Monitoring and proof of work",
        "description": "Configure idle time detection, activity and screenshot settings, and automatic alerting via Smart Notifications."
      },
      "integrations": {
        "title": "Integrations",
        "description": "Connect the tools your company already uses."
      },
      "other": {
        "title": "Other top topics",
        "description": "Schedules and shifts, Location tracking via Hubstaff's mobile app, client workflows with Clients and Project Viewers, and more."
      }
    }
  }
}
</i18n>
